import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Tilt3D from "./Tilt3D";
import {
  BLUE, BLUE_DEEP, BLUE_LIGHT, BORDER, TEXT, TEXT2, TEXT3, WHITE,
} from "../../shared";
import { setPreviewExpanded } from "../../lib/previewChrome";

const DEVICES = {
  desktop: { label: "Desktop", w: 1280, h: 800 },
  tablet:  { label: "Tablet",  w: 834,  h: 1112 },
  mobile:  { label: "Mobile",  w: 390,  h: 844 },
};

// D6 — a srcdoc iframe resolves relative URLs against an opaque base, so a
// generated site's `<link href="/style.css">` / `<img src="/logo.png">` load
// nothing and the frame paints as unstyled "raw HTML". Injecting a <base href>
// that points at the site's real origin makes those relative URLs resolve.
// No-op when we don't know the origin or the doc already declares a <base>.
function originOf(u) {
  if (!u) return "";
  try { return new URL(u).origin + "/"; } catch { return ""; }
}
function withBaseHref(html, baseHref) {
  if (!html || !baseHref || /<base[\s>]/i.test(html)) return html;
  const tag = `<base href="${baseHref}">`;
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head[^>]*>/i, (m) => `${m}${tag}`);
  if (/<html[^>]*>/i.test(html)) return html.replace(/<html[^>]*>/i, (m) => `${m}<head>${tag}</head>`);
  return `${tag}${html}`;
}

const PREVIEW_SHIMMER_CSS = `
@keyframes vellaPrevShimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }`;

export default function WebsitePreviewCard({
  html,
  url,
  title = "Untitled site",
  domain,
  onConnectDomain,
  onBuyDomain,
  onEdit,
  onRefresh,
}) {
  const [expanded, setExpanded] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [editing, setEditing] = useState(false);
  const [domainPanel, setDomainPanel] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [editableHtml, setEditableHtml] = useState(html || "");
  const [urlBar, setUrlBar] = useState(url || (domain ? `https://${domain}` : ""));
  // D6 — keep the shimmer skeleton up until the frame has loaded AND painted
  // one frame, then cross-fade the content in. Never show unstyled content.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { setEditableHtml(html || ""); }, [html]);
  useEffect(() => { setUrlBar(url || (domain ? `https://${domain}` : "")); }, [url, domain]);
  // Re-arm the skeleton whenever the previewed document changes.
  useEffect(() => { setLoaded(false); }, [iframeKey, html, url]);
  const handleFrameLoad = useCallback(() => {
    // One rAF after the load event so styles/layout have painted before fade-in.
    requestAnimationFrame(() => setLoaded(true));
  }, []);

  const close = useCallback(() => {
    setExpanded(false);
    setEditing(false);
    setDomainPanel(false);
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, close]);

  // Hide all Vella chrome while the full-bleed editor is open (D10). The chat
  // thread stays mounted underneath, so closing restores the scroll position.
  useEffect(() => {
    if (!expanded) return undefined;
    setPreviewExpanded(true);
    return () => setPreviewExpanded(false);
  }, [expanded]);

  const refresh = () => {
    setIframeKey((k) => k + 1);
    if (onRefresh) onRefresh();
  };

  const handleEdit = () => {
    if (editing) {
      setEditing(false);
    } else {
      setEditing(true);
      setDomainPanel(false);
      if (onEdit) onEdit();
    }
  };

  const dev = DEVICES[device];
  const baseHref = originOf(url);
  const previewSrc = url && !html
    ? { src: url }
    : { srcDoc: withBaseHref(editableHtml, baseHref) || "<!doctype html><html><body style=\"font-family:system-ui;display:flex;align-items:center;justify-content:center;height:100vh;color:#7C8FAB\">No content yet</body></html>" };

  return (
    <>
      <Tilt3D
        onClick={() => setExpanded(true)}
        ariaLabel={`Open website preview for ${title}`}
        role="button"
        style={{
          width: "100%",
          maxWidth: 460,
          background: "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(244,250,255,0.86) 100%)",
          border: "1px solid rgba(255,255,255,0.7)",
          boxShadow: "0 1px 0 rgba(255,255,255,0.95) inset, 0 22px 50px -22px rgba(11,107,211,0.30), 0 10px 28px -14px rgba(14,165,233,0.18)",
          overflow: "hidden",
        }}
      >
        {/* Top action bar */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 12px 8px",
          borderBottom: "1px solid rgba(190,219,247,0.45)",
        }}>
          <div style={{ display: "flex", gap: 5 }}>
            <span style={dot("#FF5F57")} />
            <span style={dot("#FEBC2E")} />
            <span style={dot("#28C840")} />
          </div>
          <div style={{
            flex: 1, minWidth: 0,
            background: "rgba(224,242,254,0.55)",
            borderRadius: 8, padding: "5px 10px",
            fontSize: 11, color: TEXT2, fontWeight: 500,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {urlBar || "preview.vella.app"}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setDomainPanel(true); setExpanded(true); }}
            style={{
              padding: "6px 12px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              color: WHITE,
              background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DEEP} 100%)`,
              border: "none",
              cursor: "pointer",
              boxShadow: "0 6px 14px -6px rgba(14,165,233,0.55)",
              whiteSpace: "nowrap",
            }}
          >
            {domain ? "Connect Domain" : "Buy Domain"}
          </button>
        </div>

        {/* Live iframe preview */}
        <div style={{ position: "relative", paddingBottom: "62.5%", background: "#0A1B3A", overflow: "hidden" }}>
          <style>{PREVIEW_SHIMMER_CSS}</style>
          <iframe
            key={iframeKey}
            title={title}
            sandbox=""
            onLoad={handleFrameLoad}
            {...previewSrc}
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              border: "none", pointerEvents: "none",
              transform: "scale(1)", transformOrigin: "top left",
              opacity: loaded ? 1 : 0,
              transition: "opacity 300ms ease",
            }}
          />
          {/* D6 — shimmer skeleton stays until the frame is loaded + painted,
              so unstyled markup never flashes through. */}
          {!loaded ? (
            <div aria-hidden="true" style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(100deg, #0E2247 30%, #1B3B72 50%, #0E2247 70%)",
              backgroundSize: "200% 100%",
              animation: "vellaPrevShimmer 1.3s linear infinite",
            }} />
          ) : null}
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(180deg, rgba(10,27,58,0) 70%, rgba(10,27,58,0.35) 100%)",
            pointerEvents: "none",
          }} />
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 14px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
            <div style={{ fontSize: 11, color: TEXT3, marginTop: 2 }}>Click to open editor</div>
          </div>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
            color: BLUE_DEEP, background: BLUE_LIGHT,
            padding: "4px 9px", borderRadius: 999, textTransform: "uppercase",
          }}>WEBSITE</div>
        </div>
      </Tilt3D>

      {expanded ? createPortal((
        <FullscreenEditor
          title={title}
          html={editableHtml}
          url={urlBar}
          onUrlChange={setUrlBar}
          device={device}
          setDevice={setDevice}
          editing={editing}
          onEdit={handleEdit}
          onHtmlChange={setEditableHtml}
          onRefresh={refresh}
          onClose={close}
          iframeKey={iframeKey}
          dev={dev}
          domainPanel={domainPanel}
          setDomainPanel={setDomainPanel}
          domain={domain}
          onConnectDomain={onConnectDomain}
          onBuyDomain={onBuyDomain}
        />
      ), document.body) : null}
    </>
  );
}

function dot(c) {
  return {
    width: 10, height: 10, borderRadius: "50%", background: c,
    boxShadow: "inset 0 0 0 0.5px rgba(0,0,0,0.08)",
  };
}

function FullscreenEditor({
  title, html, url, onUrlChange, device, setDevice,
  editing, onEdit, onHtmlChange, onRefresh, onClose, iframeKey, dev,
  domainPanel, setDomainPanel, domain, onConnectDomain, onBuyDomain,
}) {
  const codeRef = useRef(null);

  // Open the generated site in a real browser tab (blob URL).
  const openInNewTab = () => {
    try {
      const blob = new Blob([html || ""], { type: "text/html" });
      const u = URL.createObjectURL(blob);
      window.open(u, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { /* popup blocked / no blob support — ignore */ }
  };

  const domainLabel = (url || "").replace(/^https?:\/\//, "") || domain || "preview.vella.app";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Website editor"
      style={{
        // Full-bleed layer at z-index:40 (D10). All Vella chrome is hidden via
        // setPreviewExpanded, so nothing floats over it.
        position: "fixed", inset: 0, zIndex: 40,
        background: "rgba(10,27,58,0.55)",
        backdropFilter: "blur(20px) saturate(160%)",
        WebkitBackdropFilter: "blur(20px) saturate(160%)",
        display: "flex", flexDirection: "column",
        animation: "vellaFadeIn 200ms ease",
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes vellaFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes vellaSlideR { from { transform: translateX(100%); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          margin: "20px", flex: 1, display: "flex", flexDirection: "column",
          background: "rgba(255,255,255,0.92)",
          borderRadius: 18, overflow: "hidden",
          boxShadow: "0 24px 64px rgba(10,27,58,0.4)",
          border: "1px solid rgba(255,255,255,0.7)",
        }}
      >
        {/* Toolbar */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px",
          borderBottom: "1px solid rgba(190,219,247,0.5)",
          background: "linear-gradient(180deg, rgba(255,255,255,0.7) 0%, rgba(244,250,255,0.55) 100%)",
        }}>
          {/* Back to chat + domain (slim glass top bar — D10) */}
          <button
            type="button"
            onClick={onClose}
            title="Back to chat (Esc)"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
              background: WHITE, color: TEXT, border: `1px solid ${BORDER}`, cursor: "pointer",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
            </svg>
            Back to chat
          </button>
          <span style={{ fontSize: 12, fontWeight: 600, color: TEXT2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 200 }}>{domainLabel}</span>
          <span style={{ width: 1, height: 20, background: BORDER }} />
          {/* Device toggles */}
          <div style={{ display: "flex", gap: 4, background: BLUE_LIGHT, padding: 3, borderRadius: 10 }}>
            {Object.keys(DEVICES).map((k) => {
              const active = device === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setDevice(k)}
                  style={{
                    padding: "5px 11px", borderRadius: 8,
                    fontSize: 11, fontWeight: 700,
                    background: active ? WHITE : "transparent",
                    color: active ? BLUE_DEEP : TEXT2,
                    border: "none", cursor: "pointer",
                    boxShadow: active ? "0 2px 8px rgba(14,165,233,0.18)" : "none",
                  }}
                >
                  {DEVICES[k].label}
                </button>
              );
            })}
          </div>

          {/* Edit toggle */}
          <button
            type="button"
            onClick={onEdit}
            style={{
              padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700,
              background: editing ? BLUE : WHITE,
              color: editing ? WHITE : TEXT,
              border: `1px solid ${editing ? BLUE : BORDER}`,
              cursor: "pointer",
            }}
          >
            {editing ? "Done" : "Edit"}
          </button>

          {/* Refresh */}
          <button
            type="button"
            onClick={onRefresh}
            aria-label="Refresh"
            style={{
              padding: 6, borderRadius: 8,
              background: WHITE, color: TEXT,
              border: `1px solid ${BORDER}`, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>

          {/* URL bar */}
          <input
            type="text"
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            placeholder="preview.vella.app"
            style={{
              flex: 1, minWidth: 0,
              padding: "7px 12px",
              fontSize: 12, color: TEXT, fontWeight: 500,
              background: BLUE_LIGHT,
              border: `1px solid ${BORDER}`,
              borderRadius: 999,
              outline: "none",
            }}
          />

          {/* Domain CTA - only outside edit mode */}
          {!editing ? (
            <button
              type="button"
              onClick={() => setDomainPanel((v) => !v)}
              style={{
                padding: "7px 14px",
                fontSize: 12, fontWeight: 700,
                color: WHITE,
                background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DEEP} 100%)`,
                border: "none", borderRadius: 999, cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: "0 6px 14px -6px rgba(14,165,233,0.55)",
              }}
            >
              {domain ? "Connect Domain" : "Buy Domain"}
            </button>
          ) : null}

          {/* Open in a new tab */}
          <button
            type="button"
            onClick={openInNewTab}
            title="Open in a new tab"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "7px 12px", fontSize: 12, fontWeight: 700,
              color: TEXT, background: WHITE, border: `1px solid ${BORDER}`,
              borderRadius: 999, cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            Open
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </button>

          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              padding: 6, marginLeft: 4, borderRadius: 8,
              background: "transparent", color: TEXT2,
              border: `1px solid ${BORDER}`, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body: preview + optional editor */}
        <div style={{ flex: 1, display: "flex", minHeight: 0, position: "relative" }}>
          {editing ? (
            <textarea
              ref={codeRef}
              value={html}
              onChange={(e) => onHtmlChange(e.target.value)}
              spellCheck={false}
              style={{
                width: "42%",
                resize: "none",
                fontFamily: '"SF Mono", ui-monospace, "JetBrains Mono", Menlo, monospace',
                fontSize: 12,
                lineHeight: 1.55,
                color: "#0A1B3A",
                background: "rgba(244,250,255,0.7)",
                border: "none",
                borderRight: "1px solid rgba(190,219,247,0.5)",
                padding: 16,
                outline: "none",
              }}
            />
          ) : null}

          <div style={{
            flex: 1, background: "#0A1B3A",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: device === "desktop" ? 0 : 24, overflow: "auto",
          }}>
            <div style={{
              width: device === "desktop" ? "100%" : dev.w,
              height: device === "desktop" ? "100%" : dev.h,
              maxWidth: "100%", maxHeight: "100%",
              background: WHITE,
              borderRadius: device === "desktop" ? 0 : 18,
              overflow: "hidden",
              boxShadow: device === "desktop" ? "none" : "0 24px 50px rgba(0,0,0,0.45)",
              border: device === "desktop" ? "none" : "1px solid rgba(255,255,255,0.18)",
              transition: "all 240ms ease",
            }}>
              <iframe
                key={iframeKey + "-" + device}
                title="Live preview"
                sandbox="allow-same-origin allow-scripts allow-forms"
                srcDoc={withBaseHref(html, originOf(url)) || "<!doctype html><html><body></body></html>"}
                style={{ width: "100%", height: "100%", border: "none", background: WHITE }}
              />
            </div>
          </div>

          {/* Domain panel slide-in */}
          {domainPanel && !editing ? (
            <DomainPanel
              domain={domain}
              onClose={() => setDomainPanel(false)}
              onConnect={onConnectDomain}
              onBuy={onBuyDomain}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DomainPanel({ domain, onClose, onConnect, onBuy }) {
  const [search, setSearch] = useState(domain || "");
  const suggestions = search
    ? [
        { name: `${slug(search)}.com`,  price: 12.99, hot: true },
        { name: `${slug(search)}.co`,   price: 29.99 },
        { name: `${slug(search)}.shop`, price: 8.99 },
        { name: `${slug(search)}.app`,  price: 19.99 },
        { name: `try${slug(search)}.com`, price: 11.99 },
      ]
    : [];

  return (
    <div
      style={{
        position: "absolute", top: 0, right: 0, bottom: 0,
        width: 360, maxWidth: "90%",
        background: "rgba(255,255,255,0.96)",
        borderLeft: "1px solid rgba(190,219,247,0.5)",
        boxShadow: "-12px 0 32px rgba(11,107,211,0.12)",
        animation: "vellaSlideR 280ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        display: "flex", flexDirection: "column",
        zIndex: 5,
      }}
    >
      <div style={{ padding: "16px 18px", borderBottom: "1px solid rgba(190,219,247,0.5)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>Get a domain</div>
          <div style={{ fontSize: 11, color: TEXT3, marginTop: 2 }}>Free for the first year</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" style={{ background: "transparent", border: "none", cursor: "pointer", color: TEXT2, fontSize: 18, padding: 4 }}>×</button>
      </div>

      <div style={{ padding: 18 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: TEXT3, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 6, display: "block" }}>Search</label>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="yourbrand"
          autoFocus
          style={{
            width: "100%", padding: "10px 12px", fontSize: 13, color: TEXT,
            border: `1px solid ${BORDER}`, borderRadius: 10, outline: "none",
            background: WHITE,
          }}
        />

        {domain ? (
          <div style={{ marginTop: 16, padding: 12, borderRadius: 12, background: BLUE_LIGHT, border: `1px solid ${BORDER}` }}>
            <div style={{ fontSize: 11, color: TEXT3, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Connect existing</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginTop: 4 }}>{domain}</div>
            <button
              type="button"
              onClick={() => { if (onConnect) onConnect(domain); }}
              style={{
                marginTop: 10, padding: "8px 14px", borderRadius: 999,
                fontSize: 12, fontWeight: 700, color: WHITE,
                background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DEEP} 100%)`,
                border: "none", cursor: "pointer",
              }}
            >
              Connect this domain
            </button>
          </div>
        ) : null}

        <div style={{ marginTop: 18 }}>
          {suggestions.map((s) => (
            <div
              key={s.name}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 12px", borderRadius: 10,
                background: s.hot ? "rgba(224,242,254,0.55)" : "transparent",
                border: `1px solid ${s.hot ? BORDER : "transparent"}`,
                marginBottom: 4,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                <div style={{ fontSize: 11, color: TEXT3 }}>${s.price.toFixed(2)}/yr</div>
              </div>
              <button
                type="button"
                onClick={() => { if (onBuy) onBuy(s.name); }}
                style={{
                  padding: "6px 12px", borderRadius: 999,
                  fontSize: 11, fontWeight: 700,
                  color: s.hot ? WHITE : BLUE_DEEP,
                  background: s.hot ? `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DEEP} 100%)` : BLUE_LIGHT,
                  border: "none", cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Buy
              </button>
            </div>
          ))}
          {!search ? (
            <div style={{ fontSize: 12, color: TEXT3, textAlign: "center", padding: 24 }}>
              Type a name above to see availability
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function slug(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 24) || "site";
}
